import { ToastNotifications } from '@lightspeed/design-system-react'
import { T, TFunction, useT } from '@transifex/react'
import { GiftCard } from '@vend/types'
import { fetchApi, trackError } from '@vend/utilities'
import { add } from 'date-fns'
import React, { useCallback } from 'react'
import { OmniGiftCardSettings } from '../../controllers/gift-cards/balances-gift-cards'
import { getFeatureFromCache } from '../../util/feature-cache'
import { GiftCardModalFlow, GiftCardType } from './modal/GiftCardModal'
import { giftCardSettingsFromBalancesApi } from './utils'

// From GIFTCARD_PROVIDER_SETTINGS, BalancesGiftCardsController, BigCommerceGiftCertsController
export const useGiftCardProviderSettings = (
  flow: GiftCardModalFlow,
  giftCardType: GiftCardType
) => {
  const recordsTransactions =
    giftCardType === 'BIGCOMMERCE' ? false : giftCardType === 'BALANCES'

  let giftCardMinChars = 1
  if (
    giftCardType === 'BALANCES' &&
    flow === 'SELLING' &&
    !getFeatureFromCache('omni_gift_cards_allow_short_codes')
  ) {
    giftCardMinChars = 8
  }

  const giftCardMaxChars: number =
    (giftCardType === 'BIGCOMMERCE' && 15) ||
    (giftCardType === 'BALANCES' && 64) ||
    0

  const giftCardCodeCapitalised =
    (giftCardType === 'BIGCOMMERCE' && (
      <T
        _str="Gift certificate code"
        _context="BigCommerce term for gift card code capitalised"
      />
    )) ||
    (giftCardType === 'BALANCES' && (
      <T
        _str="Gift card number"
        _context="X-series term for gift card code capitalised"
      />
    )) ||
    null

  return {
    recordsTransactions,
    giftCardMinChars,
    giftCardMaxChars,
    giftCardCodeCapitalised,
  }
}

export const useGiftCardSearchStrings = (
  giftCardType: GiftCardType,
  minChars: number
) => {
  const t = useT()

  const placeholder =
    (giftCardType === 'BALANCES' &&
      t('Enter gift card number', {
        _context: 'Find X-series gift card placeholder',
      })) ||
    (giftCardType === 'BIGCOMMERCE' &&
      t('Enter gift certificate code', {
        _context: 'Find BigCommerce gift card placeholder',
      })) ||
    undefined

  const invalidCharactersErrorMessage =
    (giftCardType === 'BALANCES' && (
      <T
        _str="Gift card number can only contain letters, numbers or dashes"
        _context="X-series gift card code has invalid characters in input error message"
      />
    )) ||
    (giftCardType === 'BIGCOMMERCE' && (
      <T
        _str="Gift certificate code can only contain letters, numbers or dashes"
        _context="BigCommerce gift card code has invalid characters in input error message"
      />
    )) ||
    null

  const tooShortErrorMessage =
    (giftCardType === 'BALANCES' && (
      <T
        _str="Gift card number must contain at least {minChars} characters"
        _context="X-series gift card code input is too short error message"
        minChars={minChars}
      />
    )) ||
    (giftCardType === 'BIGCOMMERCE' && (
      <T
        _str="Gift certificate code must contain at least {minChars} characters"
        _context="BigCommerce gift card code input is too short error message"
        minChars={minChars}
      />
    )) ||
    null

  const findButtonText =
    (giftCardType === 'BALANCES' && (
      <T _str="Find gift card" _context="Find X-series gift card button" />
    )) ||
    (giftCardType === 'BIGCOMMERCE' && (
      <T
        _str="Find gift certificate"
        _context="Find BigCommerce gift card button"
      />
    )) ||
    null

  return {
    placeholder,
    invalidCharactersErrorMessage,
    tooShortErrorMessage,
    findButtonText,
  }
}

export const GIFTCARDS_ENDPOINT = '/api/2.0/balances/gift_cards'

const getOfflineErrorMessage = (t: TFunction, flow: string) => {
  return flow === 'SELLING'
    ? t('Gift cards can’t be sold when the sell screen is offline', {
        _context:
          'Trying to sell gift card when sell screen is offline error toast',
      })
    : t('Gift cards can’t be redeemed when the sell screen is offline', {
        _context:
          'Trying to redeem gift card when sell screen is offline error toast',
      })
}

const getDefaultErrorMessage = (t: TFunction) =>
  t('Something went wrong. Please try again.', {
    _context: 'Generic gift card error toast',
  })

export const useRetrieveRetailerGiftCardSettings = (
  flow: GiftCardModalFlow,
  onClose: () => void
) => {
  const t = useT()

  return useCallback(async (): Promise<OmniGiftCardSettings | null> => {
    let response: Response

    try {
      response = await fetchApi(`${GIFTCARDS_ENDPOINT}/retailer/status`)
    } catch {
      ToastNotifications.negative(getOfflineErrorMessage(t, flow))
      onClose()
      return null
    }

    if (!response.ok) {
      ToastNotifications.negative(getDefaultErrorMessage(t))
      onClose()
      return null
    }

    const apiGiftCardSettings = await response.json()
    return giftCardSettingsFromBalancesApi(apiGiftCardSettings)
  }, [flow, onClose, t])
}

export const useRetrieveGiftCard = (
  flow: GiftCardModalFlow,
  giftCardSettings: OmniGiftCardSettings | null,
  onClose: () => void
) => {
  const t = useT()

  return useCallback(
    async (code: string): Promise<GiftCard | null> => {
      let response: Response

      try {
        response = await fetchApi(`${GIFTCARDS_ENDPOINT}/${code}`)
      } catch {
        ToastNotifications.negative(getOfflineErrorMessage(t, flow))
        onClose()
        return null
      }

      if (response.status === 404) {
        if (flow === 'REDEEMING') {
          ToastNotifications.negative(
            t('Gift card not activated. Check again or enter another number.', {
              _context: 'Gift card not found error toast',
            })
          )
          onClose()
          return null
        }

        if (!giftCardSettings) {
          ToastNotifications.negative(getDefaultErrorMessage(t))
          onClose()
          return null
        }

        // SELLING
        let expiresAt = response.headers.get('expected-expiry-date')

        const { expiry_period, expiry_period_type } = giftCardSettings
        if (expiry_period && expiry_period_type) {
          // If the retailer has expiry periods set, update the expiry date
          const expiryAtPeriod = new Date()
          switch (expiry_period_type) {
            case 'DAYS':
              expiryAtPeriod.setDate(
                add(expiryAtPeriod, {
                  days: expiry_period,
                }).getDate()
              )
              break
            case 'MONTHS':
              expiryAtPeriod.setMonth(
                add(expiryAtPeriod, {
                  months: expiry_period,
                }).getMonth()
              )
              break
            case 'YEARS':
              expiryAtPeriod.setFullYear(
                add(expiryAtPeriod, {
                  years: expiry_period,
                }).getFullYear()
              )
              break
            default:
              trackError(
                `[GiftCardModal] Unknown expiry type: ${expiry_period_type}`
              )
              ToastNotifications.negative(getDefaultErrorMessage(t))
              onClose()
              return null
          }
          expiresAt = expiryAtPeriod.toISOString()
        }

        const giftCard: GiftCard = {
          // Most of these fields will be set later
          id: '',
          code,
          createdAt: '',
          expiresAt,
          initialAmount: 0,
          balance: 0,
          status: 'INACTIVE',
          transactions: [],
        }

        return giftCard
      }

      if (!response.ok) {
        // Other error responses
        ToastNotifications.negative(getDefaultErrorMessage(t))
        onClose()
        return null
      }

      // Gift card found

      // TODO: Map response to GiftCard state
      // const apiGiftCard = await response.json()
      // const giftCard = giftCardFromBalancesApi(apiGiftCard)
      // setGiftCard(giftCard)

      // TODO: Prepare transactions for display
      // TODO: Change to VIEW_DETAILS state
      // setState('VIEW_DETAILS')
      return null
    },
    [giftCardSettings, flow, onClose, t]
  )
}
